import { useCallback, useEffect, useState } from "react";
import { apiError } from "@/lib/api";
import { organizationService } from "@/services/organizationService";
import { EmptyState } from "@/components/common";
import { ListFilter, useListFilter, csvMoney } from "@/components/common/ListFilter";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Plus } from "lucide-react";
import { OrgCard, OrgFormDialog } from "./OrganizationParts";

const emptyOrg = { name: "", vat_id: "", country_code: "", document_language: "", discount_percent: "", payment_terms: "", address: "", website: "",
  shipping_address: "", shipping_contact: "",
  price_list_id: "", contacts: [], bank: { account_holder: "", bank_name: "", iban: "", bic: "" } };

const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "de");

export default function OrganizationsTab({ canEdit }) {
  const { t } = useI18n();
  const [orgs, setOrgs] = useState(null);
  const [priceLists, setPriceLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(emptyOrg);
  const [rev, setRev] = useState({ revenue: {}, years: [] });
  const [revYear, setRevYear] = useState("all");

  const load = useCallback(() => {
    organizationService.list().then((d) => setOrgs([...d].sort(byName))).catch(() => setOrgs([]));
    organizationService.priceLists().then((d) => setPriceLists([...d].sort(byName))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { organizationService.revenue(revYear).then((d) => setRev(d)).catch(() => {}); }, [revYear]);

  const openNew = () => { setEditId(null); setF(emptyOrg); setOpen(true); };
  const openEdit = (o) => {
    setEditId(o.id);
    setF({ name: o.name, vat_id: o.vat_id || "", country_code: o.country_code || "", document_language: o.document_language || "", discount_percent: o.discount_percent || "",
      payment_terms: o.payment_terms || "", address: o.address || "", website: o.website || "", price_list_id: o.price_list_id || "",
      shipping_address: o.shipping_address || "", shipping_contact: o.shipping_contact || "",
      contacts: (o.contacts || []).map((c) => ({ ...c, _k: crypto.randomUUID() })),
      bank: o.bank || { account_holder: "", bank_name: "", iban: "", bic: "" } });
    setOpen(true);
  };

  const saveOrg = async () => {
    const payload = { ...f, discount_percent: Number(f.discount_percent) || 0, price_list_id: f.price_list_id || null };
    try {
      if (editId) await organizationService.update(editId, payload);
      else await organizationService.create(payload);
      toast.success(t("common.saved")); setOpen(false); setF(emptyOrg); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delOrg = async (o) => {
    if (!window.confirm(t("sup.confirmDelete", { name: o.name }))) return;
    try { await organizationService.remove(o.id); toast.success(t("prod.deleted")); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const archOrg = async (o) => {
    try { await organizationService.archive(o.id); toast.success(t("common.archived")); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const filter = useListFilter(orgs, { fields: ["name", "vat_id", "country_code", "address", "website"], storageKey: "b2bOrgs" });

  return (
    <>
      <ListFilter filter={filter} testId="orgs" placeholder={t("filter.searchName")}
        exportName="b2b_kunden" exportColumns={[
          { label: t("common.name"), value: (o) => o.name },
          { label: "USt-IdNr.", value: (o) => o.vat_id || "" },
          { label: t("common.country"), value: (o) => o.country_code || "" },
          { label: t("ord.paymentTerms"), value: (o) => o.payment_terms || "" },
          { label: t("ord.shippingAddress"), value: (o) => (o.address || "").replace(/\s*\n\s*/g, ", ") },
          { label: t("common.revenue"), value: (o) => csvMoney(rev.revenue?.[o.id]) },
        ]} />
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t("common.revenue")}:</span>
          <Select value={revYear} onValueChange={setRevYear}>
            <SelectTrigger className="w-36 h-9" data-testid="rev-year"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("fin.allYears")}</SelectItem>{(rev.years || []).map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {canEdit && <Button data-testid="new-org-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> {t("ord.organization")}</Button>}
      </div>
      {orgs === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        orgs.length === 0 ? <EmptyState testId="orgs-empty" icon={Building2} title={t("b2b.tabCustomers")} hint={t("b2b.subtitle")} /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="orgs-list">
            {filter.filtered.map((o) => (
              <OrgCard key={o.id} o={o} canEdit={canEdit} priceLists={priceLists}
                revenue={rev.revenue?.[o.id]} revYear={revYear}
                onEdit={openEdit} onArchive={archOrg} onDelete={delOrg} />
            ))}
          </div>
        )}
      <OrgFormDialog open={open} setOpen={setOpen} editId={editId} f={f} setF={setF} priceLists={priceLists} onSave={saveOrg} />
    </>
  );
}
